import { IBrandMenuOrderItem } from '../types/brandMenu';
import { CartItem } from './cart';
import { TopLevelMenuItem } from './menu';
import { GenericMap } from './types';
import { IssueReportItem } from '../redux/features/issueReport/issueReport.props';

/**
 * HITL-1623: the options behind the issue-report item picker.
 *
 * ## Two sources, and why both are needed
 *
 * The cart alone cannot answer the question this feature exists for. "The menu has no
 * such modifier", "this is 86'd but shows as available", "we don't sell that" are all
 * reports about an item that was never IN the cart — so the picker offers the cart
 * first (the common case, a handful of lines) and then the whole menu behind it.
 *
 * ## Why the menu half also fixes the identifier problem
 *
 * `agent_cart` is `IBrandMenuOrderItem[]`, which carries a display NAME and nothing
 * else — no id, no path. It cannot be enriched: its converter is shared with
 * brand-menu order submission and the `agent_cart` event, so widening it would change
 * both of those payloads. Names do not join across brands or menu versions, which is
 * exactly what cross-store aggregation needs.
 *
 * `TopLevelMenuItem` DOES carry `id`, `category` and `pathToItem`. So a menu pick has
 * a stable identifier directly, and a CART pick borrows one by resolving its name
 * against the same map. That resolution is best-effort — `null` when the name does
 * not match — and a null is honest where a fabricated id would not be.
 */
export interface IssueItemOption extends IssueReportItem {
  /**
   * The group heading the picker renders. Kept on the option rather than derived at
   * render time because MUI's `groupBy` runs per option and consecutive equal values
   * form one group — so the ORDER of this array is what produces the two sections.
   */
  group: string;
}

export const CART_GROUP_LABEL = 'In this cart';
export const MENU_GROUP_LABEL = 'All menu items';

/**
 * `topLevelMenuItems` is not purely items: entries whose name starts with `/` are
 * slash-command prompts (`promptsSelector` in `selectors/menu` reads exactly those),
 * and offering "/upsell: would you like fries" as the item an issue is about would be
 * nonsense.
 */
const isSlashCommandPrompt = (name: string): boolean => name.startsWith('/');

/**
 * Menu items are keyed by name so a cart line can find its own id.
 *
 * FIRST ENTRY WINS on a duplicate name. The alternative — last wins — would make the
 * resolved id depend on menu iteration order, which is not stable across menu
 * versions, so a report's id could change between two runs over the same menu.
 */
export const buildMenuItemsByName = (
  topLevelMenuItems: GenericMap<TopLevelMenuItem> | undefined
): Map<string, TopLevelMenuItem> => {
  const byName = new Map<string, TopLevelMenuItem>();
  Object.values(topLevelMenuItems || {}).forEach((item) => {
    if (!item?.name || isSlashCommandPrompt(item.name)) return;
    if (!byName.has(item.name)) byName.set(item.name, item);
  });
  return byName;
};

/**
 * Resolve a display name to its stable identifiers, or to nulls.
 *
 * Exported because the same resolution has to happen for a cart pick and for a
 * gesture-filed one (HITL-1655), and two copies would drift.
 */
export const resolveIssueReportItem = (
  name: string,
  from: IssueReportItem['from'],
  byName: Map<string, TopLevelMenuItem>
): IssueReportItem => {
  const match = byName.get(name);
  return {
    name,
    from,
    menu_item_id: match?.id ?? null,
    menu_category: match?.category ?? null,
  };
};

/**
 * The picker's options: cart lines first, then every menu item that is not already
 * one of them.
 *
 * ## De-duplicated by NAME, in both halves and across them
 *
 * A cart holding two `Large Fry` lines shows `Large Fry` once. The agent cannot tell
 * two identical rows apart, so offering both only asks them to guess — and once they
 * are collapsed there is no meaningful line index left to record, which is why the
 * emitted pointer is the name and carries no position.
 *
 * The cross-half de-dupe matters more than it looks: without it every cart item
 * appears a second time under the menu heading, and the agent picking the lower one
 * would file `from: 'menu'` for an item that was plainly in the cart.
 *
 * ## Unavailable items are KEPT
 *
 * No filter on `available`. "Shows as available but the store says it is 86'd" — and
 * its inverse — is one of the reports this picker exists to carry, and filtering the
 * unavailable ones out would remove precisely those.
 */
export const buildIssueItemOptions = (
  agentCart: IBrandMenuOrderItem[] | undefined,
  topLevelMenuItems: GenericMap<TopLevelMenuItem> | undefined
): IssueItemOption[] => {
  const byName = buildMenuItemsByName(topLevelMenuItems);

  const cartNames: string[] = [];
  const seen = new Set<string>();
  (agentCart || []).forEach((line) => {
    const name = line?.item;
    if (!name || seen.has(name)) return;
    seen.add(name);
    cartNames.push(name);
  });

  const cartOptions: IssueItemOption[] = cartNames.map((name) => ({
    ...resolveIssueReportItem(name, 'cart', byName),
    group: CART_GROUP_LABEL,
  }));

  const menuOptions: IssueItemOption[] = [];
  byName.forEach((item, name) => {
    if (seen.has(name)) return;
    menuOptions.push({
      ...resolveIssueReportItem(name, 'menu', byName),
      group: MENU_GROUP_LABEL,
    });
  });

  return [...cartOptions, ...menuOptions];
};

/**
 * The item pointer for HITL-1655's cart-row flag gesture.
 *
 * ## Why this does not go through `resolveIssueReportItem`
 *
 * That function resolves a NAME against the menu, because the picker only ever has a
 * name to work with — the frozen `agent_cart` carries nothing else. A gesture is not
 * in that position: it fires on the live `CartItem`, which still holds
 * `originalMenuItemId`, `category`, `cartItemId` and `pathToItem`. So the ids are read
 * directly, and the name-matching step — the one part of the picker path that can fail
 * on a renamed or expanded display name — does not happen at all.
 *
 * `originalMenuItemId` is deliberately the field taken for `menu_item_id`: it is the
 * SAME id space as `TopLevelMenuItem.id` (both are `String(menuItemId)`), so a report
 * filed by gesture and one filed through the picker join to each other in the
 * warehouse. `CartItem.itemId` would NOT do — it is the composite `id-categoryId` key
 * the parser uses internally.
 */
export const buildCartItemPointer = (cartItem: CartItem): IssueReportItem => ({
  name: cartItem.name,
  from: 'cart',
  menu_item_id: cartItem.originalMenuItemId ?? null,
  menu_category: cartItem.category ?? null,
  cart_item_id: cartItem.cartItemId ?? null,
  path_to_item: cartItem.pathToItem ?? null,
});
